import { Card, Col, Layout, Row, Flex, List, Button, Select, Tooltip, Tabs } from 'antd';
import {
    TeamOutlined,
    IdcardOutlined,
    ClockCircleOutlined,
    ReadOutlined,
    WalletOutlined,
    UserOutlined,
    UserAddOutlined,
    UserDeleteOutlined,
    SafetyCertificateOutlined,
    InfoCircleOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
} from '@ant-design/icons';
import React, { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import VirtualList from 'rc-virtual-list';
import { toPng } from 'html-to-image';
import { get2DecimalPointsWithCommas } from 'components/numberFormatDigits';
import { t } from 'i18next';
import { DASHBOARD_SLICE } from '../slices/dashboardSlice';
import { useSelector } from 'react-redux';


const VISIBLE_TYPES = ['ประจำ', 'ชั่วคราว', 'ฝึกงาน'];

const TYPE_META = {
    'ประจำ': { icon: <UserOutlined />, bg: '#E9F7EF', color: '#24E08F' },
    'ชั่วคราว': { icon: <IdcardOutlined />, bg: '#FFF3E6', color: '#FFA152' },
    'ฝึกงาน': { icon: <ReadOutlined />, bg: '#F1EBFF', color: '#AF58FF' },
    'รายวัน': { icon: <UserOutlined />, bg: '#E9F7EF', color: '#24E08F' },
    'พาร์ทไทม์': { icon: <IdcardOutlined />, bg: '#FFF3E6', color: '#FFA152' },
};
function getTypeMeta(type) {
    const key = (type ?? '').replace('พนักงาน', '').trim();
    return TYPE_META[key] ?? { icon: <IdcardOutlined />, bg: '#f5f5f5', color: '#8c8c8c' };
}

function StatCard({ icon, bg, color, label, value, unit, onClick }) {
    return (
        <Card
            bodyStyle={{ padding: '16px 20px' }}
            style={{
                boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.06)',
                cursor: onClick ? 'pointer' : 'default',
            }}
            onClick={onClick}
        >
            <Flex align="center" gap={12}>
                <Flex
                    justify="center"
                    align="center"
                    style={{ width: 44, height: 44, borderRadius: '50%', background: bg, color, fontSize: 20 }}
                >
                    {icon}
                </Flex>
                <Flex vertical>
                    <span style={{ color: '#8c8c8c', fontSize: 13 }}>{label}</span>
                    <span style={{ fontSize: 20, fontWeight: 700 }}>
                        {value} {unit}
                    </span>
                </Flex>
            </Flex>
        </Card>
    );
}
// StatCard variant with a "เทียบกับช่วงเดียวกันปีก่อน" change indicator, used in the turnover card
function TurnoverStatCard({ icon, bg, color, label, value, unit, changePct, t, onClick }) {
    const isUp = Number(changePct) >= 0;
    const changeColor = isUp ? '#24E08F' : '#FF5858';
    return (
        <Card
            bodyStyle={{ padding: '16px 20px' }}
            style={{
                boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.06)',
                height: '100%',
                background: bg,
                cursor: onClick ? 'pointer' : 'default',
            }}
            onClick={onClick}
        >
            <Flex align="center" gap={12}>
                <Flex
                    justify="center"
                    align="center"
                    style={{ width: 44, height: 44, borderRadius: '50%', background: bg, color, fontSize: 20 }}
                >
                    {icon}
                </Flex>
                <Flex vertical>
                    <span style={{ color: '#8c8c8c', fontSize: 13 }}>{label}</span>
                    <span style={{ fontSize: 20, fontWeight: 700 }}>
                        {value} {unit}
                    </span>
                    {typeof changePct === 'number' && (
                        <span style={{ fontSize: 12, color: changeColor }}>
                            {isUp ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(changePct).toFixed(2)}%{' '}
                            <span style={{ color: '#8c8c8c' }}>
                                {t('dashboard.label.vs_same_period_last_year', t('samePeriodFastYear'))}
                            </span>
                        </span>
                    )}
                </Flex>
            </Flex>
        </Card>
    );
}

// Legend list สไตล์รูปที่ 2 (จุดสี + label + จำนวน + %)
function RatioLegend({ items, totalLabel }) {
    const total = items.reduce((sum, o) => sum + Number(o.count || 0), 0);
    return (
        <Flex vertical gap={10} style={{ minWidth: 180 }}>
            {items.map((o, i) => (
                <Flex key={i} align="center" gap={8}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: o.color, display: 'inline-block' }} />
                    <span style={{ fontSize: 13 }}>
                        {o.label} {o.count} {t('person')}{' '}
                        <span style={{ color: '#8c8c8c' }}>
                            ({total ? ((o.count / total) * 100).toFixed(2) : 0}%)
                        </span>
                    </span>
                </Flex>
            ))}
        </Flex>
    );
}

function LayoutDashboard({
    PieChart = () => { },
    NationalityChart = () => { },
    TenureCard = () => { },
    AgeRangeChart = () => { },
    PieChartType = () => { },
    BranchBreakdownChart = () => { },
    DepartmentBreakdownChart = () => { },
    ColumnChart = () => { },
    dataPieType = [],
    employeeSalaryByBranch = [],
    dataPie = [],
    dataPieTypeMapped = [],
    newEmployee,
    pagination = {},
    downloadHrDashboardExcel = () => { },
    setPagination = () => { },
    dataSource = [],
    onClickCustomer = () => { },
    employeeStatusTypeOptions = [],
    // NEW props
    onClickNewHireCard = () => { },
    onClickResignedCard = () => { },
    TurnoverTrendChart = () => { },
    RecentHiresList = () => { },
    turnoverStats = { newHireCount: 0, leaveCount: 0, retentionRate: 0, newHireChangePct: 0, leaveChangePct: 0 },
    dataRecentHires = [],
    turnoverPeriodMonths = 1,
    setTurnoverPeriodMonths = () => { },
    turnoverYear,
    setTurnoverYear = () => { },
    turnoverYearOptions = [],
    SalaryTrendChart = () => { },
    salaryPeriodMonths = 12,
    setSalaryPeriodMonths = () => { },
    salaryYear,
    setSalaryYear = () => { },
}) {
    const { t, i18n } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    function formatDate(dateString) {
        if (!dateString) return;
        const date = dayjs(dateString);
        return i18n.language === 'en'
            ? date.format('DD/MM/YYYY')
            : date.format(`DD/MM/${date.year() + 543}`);
    }

    const totalEmployee = useMemo(
        () => dataPieType?.reduce((p, c) => p + Number(c.count || 0), 0) ?? 0,
        [dataPieType]
    );

    const genderLegendItems = useMemo(
        () =>
            dataPie?.map((o) => ({
                label: i18n.language === 'en' ? o.typeEn : o.type,
                count: o.count,
                color: o.color,
            })) ?? [],
        [dataPie, i18n.language]
    );
    const { payrollPeriodYearRaw, payrollPeriodYearRawPrevYear } = useSelector((state) => state?.[DASHBOARD_SLICE]);
    const totalExpense = useMemo(
        () =>
            payrollPeriodYearRaw
                ?.map((o) => Number(o.totalPaid))
                ?.filter((n) => !isNaN(n))
                ?.reduce((p, c) => p + c, 0) ?? 0,
        [payrollPeriodYearRaw]
    );
    const downloadImage = () => {
        const chartElement = document.getElementById('dashboard');
        if (!chartElement) return;
        setIsLoading(true);

        const originalWidth = chartElement.style.width;
        const originalHeight = chartElement.style.height;
        chartElement.style.width = `${chartElement.scrollWidth}px`;
        chartElement.style.height = `${chartElement.scrollHeight}px`;
        const scale = 2;

        toPng(chartElement, {
            width: chartElement.scrollWidth * scale,
            height: chartElement.scrollHeight * scale,
            style: {
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                width: `${chartElement.scrollWidth}px`,
                height: `${chartElement.scrollHeight}px`,
            },
        })
            .then((dataUrl) => {
                chartElement.style.width = originalWidth;
                chartElement.style.height = originalHeight;
                const link = document.createElement('a');
                link.href = dataUrl;
                link.download = 'dashboard-chart.png';
                link.click();
            })
            .catch((error) => console.error('Could not generate image', error))
            .finally(() => setIsLoading(false));
    };

    return (
        <Layout id="dashboard">
            <Layout.Content>
                <Flex vertical gap={12}>
                    <Flex justify="space-between" align="center">
                        <h2 style={{ margin: 0 }}>
                            {t('dashboard.label.thisdate', 'ข้อมูล ณ วันที่')}{' '}
                            {dayjs().locale(i18n.language).format(`DD MMMM ${i18n.language === 'en' ? 'YYYY' : 'BBBB'}`)}
                        </h2>
                        <Button onClick={downloadHrDashboardExcel} loading={isLoading} disabled={isLoading}>
                            {t('common.download', 'ดาวน์โหลด')}
                        </Button>
                    </Flex>

                    <Row gutter={[12, 12]}>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <StatCard
                                icon={<TeamOutlined />}
                                bg="#E9F3FF"
                                color="#47B2FF"
                                label={t('dashboard.label.total_employee', t('totalEmployee'))}
                                value={totalEmployee}
                                unit={t('unit.person', t('person'))}
                            />
                        </Col>
                        {dataPieTypeMapped?.map((o, i) => {
                            const label = i18n.language === 'en' ? o.nameEn : o.name;
                            return (
                                <Col xs={24} sm={12} md={8} lg={6} key={i}>
                                    <StatCard
                                        icon={getTypeMeta(o.name).icon}
                                        bg={`${o.color}22`}
                                        color={o.color}
                                        label={label}
                                        value={o.count}
                                        unit={t('unit.person', t('person'))}
                                        onClick={() => {
                                            const clicked = {
                                                series: 'listEmployeeTypeId',
                                                value: [o.id],
                                            };
                                            onClickCustomer(clicked);
                                        }}
                                    />
                                </Col>
                            );
                        })}
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <StatCard
                                icon={<WalletOutlined />}
                                bg="#FDEDED"
                                color="#FF5858"
                                label={t('dashboard.label.total_expense', 'ค่าใช้จ่ายรวม')}
                                value={get2DecimalPointsWithCommas(totalExpense)}
                                unit={t('unit.baht', 'บาท')}
                            />
                        </Col>
                    </Row>

                    {/* แทนที่ Row 2 (donut เพศ+ประเภท) และ Row สัญชาติ+อายุเดิม ด้วยแถวเดียว 4 คอลัมน์ */}
                    {/* <Row gutter={[12, 12]}>
                        <Col xs={24} sm={12} lg={5}>
                            <Card
                                title={t('dashboard.label.static_employee', 'สัดส่วนพนักงาน')}
                                style={{ boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.06)', height: '100%' }}
                            >
                                <PieChart />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={5}>
                            <Card
                                title={t('dashboard.label.nationality', t('empolyeeNationality'))}
                                style={{ boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.06)', height: '100%' }}
                            >
                                <NationalityChart />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={8}>
                            <Card
                                title={t('dashboard.label.age_range', t('ageRange'))}
                                style={{ boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.06)', height: '100%' }}
                            >
                                <AgeRangeChart />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card
                                title={t('dashboard.label.static_employee_type', 'จำแนกตามประเภทพนักงาน')}
                                style={{ boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.06)', height: '100%' }}
                            >
                                <PieChartType />
                            </Card>
                        </Col>
                    </Row> */}
                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                        <Col span={12}>
                            <Card title={t('employeeDemographic')}>

                                <Tabs
                                    items={[
                                        {
                                            key: 'gender',
                                            label: t('genDer'),
                                            children: <PieChart />,
                                        },
                                        {
                                            key: 'age',
                                            label: t('age'),
                                            children: <AgeRangeChart />,
                                        },
                                        {
                                            key: 'type',
                                            label: t('employeeType'),
                                            children: <PieChartType />,
                                        },
                                        {
                                            key: 'nation',
                                            label: t('nationality'),
                                            children: <NationalityChart />,
                                        },
                                    ]}
                                />

                            </Card>
                        </Col>

                        <Col span={12}>
                            <Card title={t('employeeDistribution')}>

                                <Tabs
                                    items={[
                                        {
                                            key: 'branch',
                                            label: t('byBranch'),
                                            children: <BranchBreakdownChart />
                                        },
                                        {
                                            key: 'department',
                                            label: t('byDepartment'),
                                            children: <DepartmentBreakdownChart />
                                        },
                                        {
                                            key: 'tenure',
                                            label: t('byTenure'),
                                            children: <TenureCard />
                                        }
                                    ]}
                                />

                            </Card>
                        </Col>
                    </Row>
                    {/* Row 3: ค่าใช้จ่าย + พนักงานลาออก (เหมือนเดิม ปรับ style เล็กน้อย) */}
                    <Row gutter={[12, 12]} style={{ minHeight: 500 }}>
                        <Col span={12}>
                            <Card
                                title={t('dashboard.label.salary_trend', t('salaryTrend'))}
                                style={{ boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.06)', height: '100%' }}
                                extra={
                                    <Flex align="center" gap={8}>
                                        <Flex style={{ background: '#f0f2f5', borderRadius: 6, padding: 2 }}>
                                            {[
                                                { value: 1, label: t('dashboard.label.latest_month', 'เดือนปัจจุบัน') },
                                                { value: 3, label: `3 ${t('unit.month', 'เดือน')}` },
                                                { value: 6, label: `6 ${t('unit.month', 'เดือน')}` },
                                                { value: 12, label: `12 ${t('unit.month', 'เดือน')}` },
                                            ].map((opt) => (
                                                <Button
                                                    key={opt.value}
                                                    type={salaryPeriodMonths === opt.value ? 'primary' : 'text'}
                                                    size="small"
                                                    onClick={() => setSalaryPeriodMonths(opt.value)}
                                                >
                                                    {opt.label}
                                                </Button>
                                            ))}
                                        </Flex>
                                        <Select
                                            size="small"
                                            value={salaryYear}
                                            onChange={setSalaryYear}
                                            style={{ width: 100 }}
                                            options={turnoverYearOptions.map((y) => ({ label: `${t('dashboard.label.year', t('year'))} ${y}`, value: y }))}
                                        />
                                    </Flex>
                                }
                            >
                                <SalaryTrendChart />
                            </Card>
                        </Col>
                        {/* <Col span={8}>
                            <Card
                                title={t('dashboard.label.terminate')}
                                style={{ boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.06)', height: '100%' }}
                            >
                                <List>
                                    <VirtualList data={dataSource} height={390} itemKey="id" virtual={false}>
                                        {(item) => {
                                            const startDate = dayjs(item.employmentDate);
                                            const endDate = dayjs(item.effectiveDate);
                                            const years = endDate.diff(startDate, 'year');
                                            const months = endDate.diff(startDate.add(years, 'year'), 'month');
                                            const days = endDate.diff(startDate.add(years, 'year').add(months, 'month'), 'day');
                                            let workLive = '';
                                            if (years > 0) workLive += years + (i18n.language === 'en' ? ' year ' : ' ปี ');
                                            if (months > 0) workLive += months + (i18n.language === 'en' ? ' month ' : ' เดือน ');
                                            if (days > 0) workLive += days + (i18n.language === 'en' ? ' days ' : ' วัน ');

                                            return (
                                                <Card style={{ backgroundColor: '#DBEDFF', marginTop: 5 }}>
                                                    <Flex
                                                        vertical
                                                        onClick={() => {
                                                            const clicked = {
                                                                series: 'listEmployeeStatus',
                                                                value: [
                                                                    employeeStatusTypeOptions?.find(
                                                                        ({ code }) => code === 'EMPLOYEE_STATUS_TYPE_03'
                                                                    )?.id,
                                                                ],
                                                            };
                                                            onClickCustomer(clicked);
                                                        }}
                                                    >
                                                        <span style={{ fontWeight: 'bold' }}>
                                                            {i18n.language === 'en'
                                                                ? `${item.nameEn} ${item.surNameEn}`
                                                                : `${item.name} ${item.surName}`}
                                                        </span>
                                                        <span>{t('dashboard.label.date_terminate')}: {formatDate(item.effectiveDate)}</span>
                                                        <span>{t('dashboard.label.work_start_date')}: {formatDate(item.employmentDate)}</span>
                                                        <span>
                                                            {t('dashboard.label.workplace')}:{' '}
                                                            {i18n.language === 'en' ? item?.branchInfo?.branchNameEn : item?.branchInfo?.branchName}
                                                        </span>
                                                        <span>{t('dashboard.label.year_of_service')}: {workLive.trim()}</span>
                                                        <span>
                                                            {t('dashboard.label.operator')}:{' '}
                                                            {i18n.language === 'en'
                                                                ? `${item?.createInfo?.nameEn} ${item?.createInfo?.surNameEn}`
                                                                : `${item?.createInfo?.name} ${item?.createInfo?.surName}`}
                                                        </span>
                                                    </Flex>
                                                </Card>
                                            );
                                        }}
                                    </VirtualList>
                                </List>
                            </Card>
                        </Col> */}

                        <Col span={12}>
                            <Card
                                style={{ boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.06)', height: '100%' }}
                                title={
                                    <Flex align="center" gap={6}>
                                        <span>{t('dashboard.label.employee_in_out', t('employeeInOut'))}</span>
                                        <Tooltip title={t('dashboard.label.employee_in_out_tooltip', t('employeeInOutTooltip'))}>
                                            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 13 }} />
                                        </Tooltip>
                                    </Flex>
                                }
                                extra={
                                    <Flex align="center" gap={8}>
                                        <Flex style={{ background: '#f0f2f5', borderRadius: 6, padding: 2 }}>
                                            {[
                                                { value: 1, label: t('dashboard.label.latest_month', 'เดือนปัจจุบัน') },
                                                { value: 3, label: `3 ${t('unit.month', 'เดือน')}` },
                                                { value: 6, label: `6 ${t('unit.month', 'เดือน')}` },
                                                { value: 12, label: `12 ${t('unit.month', 'เดือน')}` },
                                            ].map((opt) => (
                                                <Button
                                                    key={opt.value}
                                                    type={turnoverPeriodMonths === opt.value ? 'primary' : 'text'}
                                                    size="small"
                                                    onClick={() => setTurnoverPeriodMonths(opt.value)}
                                                    style={{ boxShadow: 'none' }}
                                                >
                                                    {opt.label}
                                                </Button>
                                            ))}
                                        </Flex>
                                        <Select
                                            size="small"
                                            value={turnoverYear}
                                            onChange={setTurnoverYear}
                                            style={{ width: 100 }}
                                            options={turnoverYearOptions.map((y) => ({
                                                label: `${t('dashboard.label.year', t('year'))} ${y}`,
                                                value: y,
                                            }))}
                                        />
                                    </Flex>
                                }
                            >
                                <Flex vertical gap={16}>
                                    <Row gutter={[12, 12]}>
                                        <Col flex="1">
                                            <TurnoverStatCard
                                                t={t}
                                                icon={<UserAddOutlined />}
                                                bg="#E9F7EF"
                                                color="#24E08F"
                                                label={t('dashboard.label.new_hire', t('newEmployee'))}
                                                value={turnoverStats.newHireCount}
                                                unit={t('unit.person', t('person'))}
                                                changePct={turnoverStats.newHireChangePct}
                                                onClick={onClickNewHireCard}
                                            />
                                        </Col>
                                        <Col flex="1">
                                            <TurnoverStatCard
                                                t={t}
                                                icon={<UserDeleteOutlined />}
                                                bg="#FDEDED"
                                                color="#FF5A9E"
                                                label={t('dashboard.label.leave', t('employeesResigned'))}
                                                value={turnoverStats.leaveCount}
                                                unit={t('unit.person', t('person'))}
                                                changePct={turnoverStats.leaveChangePct}
                                                onClick={onClickResignedCard}
                                            />
                                        </Col>
                                        {/* <Col flex="1">
                                            <StatCard
                                                icon={<SafetyCertificateOutlined />}
                                                bg="#F1EBFF"
                                                color="#AF58FF"
                                                label={t('dashboard.label.retention_rate', 'อัตราการคงอยู่ของพนักงาน')}
                                                value={`${Number(turnoverStats.retentionRate || 0).toFixed(2)}%`}
                                                unit=""
                                            />
                                        </Col> */}
                                    </Row>
                                    <div>
                                        <span style={{ fontWeight: 600, fontSize: 14 }}>
                                            {t('dashboard.label.employee_in_out_trend', t('employeeTurnoverTrends'))}
                                        </span>
                                        <TurnoverTrendChart />
                                    </div>
                                </Flex>
                            </Card>
                        </Col>
                    </Row>
                </Flex>
            </Layout.Content>
        </Layout>
    );
}

export default LayoutDashboard;